import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL, { getAuthHeaders } from '../utils/api';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  AddCircle as AddIcon,
  Description as FileIcon,
  TrendingUp as TrendingIcon,
  Edit as EditIcon
} from '@mui/icons-material';

function Dashboard() {
  const navigate = useNavigate();
  const [presupuestos, setPresupuestos] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      const [presupuestosRes, materialesRes] = await Promise.all([
        fetch(`${API_URL}/presupuestos`, { headers }),
        fetch(`${API_URL}/materiales`, { headers })
      ]);

      if (presupuestosRes.ok) {
        const presupuestosData = await presupuestosRes.json();
        setPresupuestos(Array.isArray(presupuestosData) ? presupuestosData : []);
      }

      if (materialesRes.ok) {
        const materialesData = await materialesRes.json();
        setMateriales(materialesData.data || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPresupuestos = presupuestos.reduce((sum, p) => sum + (p.importe_total || 0), 0);

  const stats = [
    { title: 'Presupuestos Activos', value: presupuestos.length.toString(), color: '#1976d2' },
    { title: 'Total en Presupuestos', value: `$${totalPresupuestos.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`, color: '#388e3c' },
    { title: 'Materiales en Catálogo', value: materiales.length.toString(), color: '#f57c00' },
    { title: 'Estado Activo', value: 'Campeche', color: '#7b1fa2' }
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>

      <Grid container spacing={3}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom>
                {stat.title}
              </Typography>
              <Typography variant="h3" style={{ color: stat.color }}>
                {stat.value}
              </Typography>
            </Paper>
          </Grid>
        ))}

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AddIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Crear Nuevo Presupuesto
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Carga un catálogo de conceptos en PDF o Excel y genera un presupuesto
                completo con análisis de precios unitarios usando IA.
              </Typography>
            </CardContent>
            <CardActions>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/nuevo-presupuesto')}
              >
                Iniciar Presupuesto
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <AssessmentIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Análisis Predictivo
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                El sistema utiliza inteligencia artificial para predecir precios,
                identificar materiales y generar análisis de PU basados en la base
                de datos de Campeche.
              </Typography>
            </CardContent>
            <CardActions>
              <Button variant="outlined" color="secondary">
                Ver Documentación
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Presupuestos Recientes
            </Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : presupuestos.length === 0 ? (
              <>
                <Typography variant="body2" color="textSecondary">
                  No hay presupuestos creados aún. Crea tu primer presupuesto para empezar.
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    startIcon={<FileIcon />}
                    onClick={() => navigate('/nuevo-presupuesto')}
                  >
                    Cargar Catálogo de Conceptos
                  </Button>
                </Box>
              </>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Código</TableCell>
                      <TableCell>Nombre de Obra</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell>Ubicación</TableCell>
                      <TableCell>Total</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Fecha</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {presupuestos.map((presupuesto) => (
                      <TableRow key={presupuesto.id}>
                        <TableCell>{presupuesto.codigo}</TableCell>
                        <TableCell>{presupuesto.nombre_obra}</TableCell>
                        <TableCell>{presupuesto.cliente}</TableCell>
                        <TableCell>{presupuesto.ubicacion}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            ${(presupuesto.importe_total || 0).toLocaleString('es-MX', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={presupuesto.estados?.nombre || 'BORRADOR'}
                            color={presupuesto.estado_id === 1 ? 'warning' : 'primary'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(presupuesto.fecha_elaboracion).toLocaleDateString('es-MX')}
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            size="small"
                            startIcon={<EditIcon />}
                            onClick={() => navigate(`/presupuesto/${presupuesto.id}`)}
                          >
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default Dashboard;